/**
 * Match lore — the post-FT, audited club-memory surfaces (spec §5 Showcase).
 *
 *   • LoreEntryPage   — one archived match, in the EntryPage rhythm: result hero
 *                       → mood arc → prediction-vs-outcome → aggregate memory →
 *                       summary. Reuses the codex entry-page chrome.
 *   • H2HFeel         — "How we feel about them": the proprietary emotional H2H
 *                       (calibration / mood signature / reaction curve), each
 *                       carrying its sample-size caveat. Honest empty state when
 *                       there's no lore for the opponent yet.
 *   • InTheBookCard   — the bridge from a closed live thread to its lore entry.
 *
 * Editorial, hairline-separated, NOT boxes — cream programme paper, red as ink,
 * gold leaf. Every metric degrades to an honest empty state with the empty seed.
 */
import { DetailHeader, Rule } from './chrome';
import { ShareButton } from './ShareButton';
import { loreLink } from '../lib/share';
import { aggregateH2H } from '../lib/lore';
import { wasParticipated } from '../lib/participation';
import type { H2HMetric, LoreEntry, LoreMoodSample, Mood } from '../lib/types';

const RES_CLASS: Record<'W' | 'D' | 'L', string> = { W: 'res-w', D: 'res-d', L: 'res-l' };
const MOOD_WORD: Record<Mood, string> = {
  buzzing: 'buzzing',
  relieved: 'relieved',
  anxious: 'anxious',
  frustrated: 'frustrated',
};
const PHASE_WORD: Record<LoreMoodSample['t'], string> = { pre: 'Kickoff', ht: 'Half-time', ft: 'Full-time' };

const ALL_MOODS: Mood[] = ['buzzing', 'relieved', 'anxious', 'frustrated'];

// ── Mood arc sparkline ────────────────────────────────────────────────────────
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const MoodArcSpark = ({ samples }: { samples: LoreMoodSample[] }) => {
  if (samples.length < 2) return null;
  const n = samples.length;
  const points = samples
    .map((s, i) => {
      const x = n === 1 ? 50 : (i / (n - 1)) * 100;
      const y = (1 - clamp(s.avg, 0, 10) / 10) * 40;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg
      className="mood-arc-spark"
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--gold)"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {samples.map((s, i) => {
        const x = n === 1 ? 50 : (i / (n - 1)) * 100;
        const y = (1 - clamp(s.avg, 0, 10) / 10) * 40;
        return <circle key={s.t} cx={x} cy={y} r="2" fill="var(--gold)" vectorEffect="non-scaling-stroke" />;
      })}
    </svg>
  );
};
// Mirror lore.ts dominantMood: an all-zero sample (e.g. a future n===0 FT-rollup
// row) has no dominant mood — return null so we render "quiet", never a phantom
// "buzzing" beside "0 fans".
const dominant = (s: LoreMoodSample): Mood | null => {
  let best: Mood = ALL_MOODS[0]!;
  for (const m of ALL_MOODS) if ((s.moods[m] ?? 0) > (s.moods[best] ?? 0)) best = m;
  return s.moods[best] && s.moods[best]! > 0 ? best : null;
};

// ── The lore entry page ──────────────────────────────────────────────────────
export const LoreEntryPage = ({
  entry,
  onBack,
  backLabel = 'The Vault',
  trail,
}: {
  entry: LoreEntry;
  onBack: () => void;
  /** Label for the back button — names where this step returns to. */
  backLabel?: string;
  /** The full ancestor path for the "where am I" breadcrumb. */
  trail?: string[];
}) => {
  const { result, prediction, moodArc } = entry;
  const letter = result.r;
  const home = result.score.home;
  const away = result.score.away;
  const rolled = new Date(entry.verification.rolledAt);
  const youWereThere = wasParticipated(entry.matchId);

  return (
    <div className="screen detail-screen entry-screen lore-screen">
      <DetailHeader
        onBack={onBack}
        backLabel={backLabel}
        trail={trail}
        right={
          <ShareButton
            ghost
            label="Share"
            moment={{
              title: `COYG — Arsenal ${home}–${away} ${entry.opponent}`,
              text: entry.summary,
              url: loreLink(entry.matchId),
              card: {
                eyebrow: `${entry.comp} · FROM THE VAULT`,
                headline: `Arsenal ${home}–${away} ${entry.opponent}`,
                detail: entry.summary,
                stamp: 'CLUB MEMORY · COYG.ORG',
              },
            }}
          />
        }
      />

      {/* Result head — cream editorial rhythm (kicker → tie → score figures),
          matching the player page. Red is ink here, not a fill; the result
          letter tints only the score. */}
      <header className="player-head lore-head">
        <span className="player-head-num" aria-hidden="true">
          {home}–{away}
        </span>
        <span className="player-kicker">{entry.comp}</span>
        <h2 className="player-title">
          Arsenal {entry.venue === 'A' ? '@' : 'v'} {entry.opponent}
        </h2>
        <div className={`lore-head-score ${RES_CLASS[letter]}`}>
          {home}
          <span>–</span>
          {away}
        </div>
        <p className="player-line">
          <span>
            {rolled.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <span aria-hidden="true">·</span>
          <span>{entry.takeCount.toLocaleString()} takes archived</span>
          {entry.illustrative && <span className="lore-sample">Sample</span>}
        </p>
      </header>

      {youWereThere && (
        <p className="lore-you-were-there">You were in the North Bank for this one.</p>
      )}

      {/* Mood arc — the Gauge as a curve, kickoff→FT. */}
      {moodArc.samples.length > 0 && (
        <>
          <Rule label="The mood, kickoff to full-time" accent="#EF0107" />
          <MoodArcSpark samples={moodArc.samples} />
          <ol className="mood-arc">
            {moodArc.samples.map((s) => {
              const d = dominant(s);
              return (
                <li key={s.t} className="mood-arc-step">
                  <span className="mood-arc-phase">{PHASE_WORD[s.t]}</span>
                  <span className="mood-arc-avg">{s.avg.toFixed(1)}</span>
                  <span className={`mood-arc-mood${d ? ` mood--${d}` : ''}`}>{d ? MOOD_WORD[d] : 'quiet'}</span>
                  <span className="mood-arc-n">{s.n.toLocaleString()} fans</span>
                </li>
              );
            })}
          </ol>
        </>
      )}

      {/* Prediction vs outcome — what the North Bank called. */}
      <Rule label="What the North Bank called" accent="var(--gold)" />
      {prediction.total > 0 ? (
        <div className="lore-call">
          <div className="lore-call-bars">
            {(['W', 'D', 'L'] as const).map((k) => {
              const pct = Math.round((prediction.counts[k] / prediction.total) * 100);
              return (
                <div key={k} className={`lore-call-row${prediction.called === k ? ' lore-call-row--called' : ''}`}>
                  <span className="lore-call-key">{k === 'W' ? 'Win' : k === 'D' ? 'Draw' : 'Loss'}</span>
                  <span className="lore-call-bar">
                    <progress className={`lore-call-fill ${RES_CLASS[k]}`} value={pct} max="100">{pct}%</progress>
                  </span>
                  <span className="lore-call-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
          <p className="lore-call-verdict">
            {prediction.called
              ? prediction.correct
                ? `The North Bank called it${prediction.called === 'W' && prediction.confidence != null ? ` — ${prediction.confidence}% backed the win` : ''}.`
                : `The North Bank backed a ${prediction.called === 'W' ? 'win' : prediction.called === 'D' ? 'draw' : 'loss'}, and the match caught it out.`
              : 'No clear call — the North Bank was split.'}{' '}
            <em>{prediction.total.toLocaleString()} calls.</em>
          </p>
        </div>
      ) : (
        <p className="empty-note">No predictions were logged for this one.</p>
      )}

      {/* Summary — the deterministic close. */}
      <Rule label="In the book" accent="var(--gold)" />
      <blockquote className="oracle">
        <p>{entry.summary}</p>
      </blockquote>

      <p className="provenance">
        {entry.illustrative ? 'Illustrative sample' : 'Rolled at full-time'} ·{' '}
        {entry.verification.status === 'verified' ? 'verified' : 'unverified'}
        {entry.verification.status === 'verified' && ' ✓'}
      </p>
    </div>
  );
};

// ── A tappable Vault row for one archived match ──────────────────────────────
export const LoreRow = ({ entry, onOpen }: { entry: LoreEntry; onOpen: (matchId: string) => void }) => (
  <button className="vault-row lore-row" onClick={() => onOpen(entry.matchId)}>
    <span className="vault-row-title">
      Arsenal {entry.result.score.home}–{entry.result.score.away} {entry.opponent}
      {entry.illustrative && <span className="lore-sample lore-sample--inline">Sample</span>}
    </span>
    <span className="vault-row-sub">
      {entry.comp} · {entry.venue} · {entry.takeCount.toLocaleString()} takes
    </span>
  </button>
);

// ── "How we feel about them" — the emotional H2H ─────────────────────────────
const MetricLine = ({ metric, ok, raw }: { metric: H2HMetric; ok: (v: number) => string; raw: () => string }) => {
  if (metric.caveat === 'off') {
    return <p className="feel-metric feel-metric--muted">Available once live timing is on.</p>;
  }
  if (metric.caveat === 'none' || metric.value === null) {
    return <p className="feel-metric feel-metric--muted">Not enough calls yet.</p>;
  }
  if (metric.caveat === 'raw') {
    return (
      <p className="feel-metric">
        {raw()} <span className="feel-n">— too early to call a trend</span>
      </p>
    );
  }
  return (
    <p className="feel-metric">
      {ok(metric.value)} <span className="feel-n">({metric.n} games)</span>
    </p>
  );
};

export const H2HFeel = ({ entries, opponent }: { entries: LoreEntry[]; opponent: string }) => {
  const h = aggregateH2H(entries, opponent);

  if (h.n === 0) {
    return (
      <>
        <Rule label="How we feel about them" accent="var(--red-deep)" />
        <p className="empty-note">Once we've played them, the North Bank's read lands here.</p>
      </>
    );
  }

  return (
    <>
      <Rule label="How we feel about them" accent="var(--red-deep)" />
      <div className="feel">
        <div className="feel-block">
          <span className="feel-label">Calling them right</span>
          <MetricLine
            metric={h.calibration}
            ok={(v) => `The North Bank called ${opponent} games right ${v}%`}
            raw={() => `Called right ${h.calibration.fraction ?? ''}`}
          />
        </div>

        <div className="feel-block">
          <span className="feel-label">The mood signature</span>
          {h.mood.home.mood || h.mood.away.mood ? (
            <p className="feel-metric">
              {h.mood.home.mood && (
                <>
                  At home, mostly <b className={`mood--${h.mood.home.mood}`}>{MOOD_WORD[h.mood.home.mood]}</b>{' '}
                  <span className="feel-n">({h.mood.home.n} game{h.mood.home.n === 1 ? '' : 's'})</span>.{' '}
                </>
              )}
              {h.mood.away.mood && (
                <>
                  Away, mostly <b className={`mood--${h.mood.away.mood}`}>{MOOD_WORD[h.mood.away.mood]}</b>{' '}
                  <span className="feel-n">({h.mood.away.n} trip{h.mood.away.n === 1 ? '' : 's'})</span>.
                </>
              )}
            </p>
          ) : (
            <p className="feel-metric feel-metric--muted">—</p>
          )}
        </div>

        <div className="feel-block">
          <span className="feel-label">When they score first</span>
          {h.reaction.metric.caveat === 'off' ? (
            <p className="feel-metric feel-metric--muted">Available once live timing is on.</p>
          ) : h.reaction.conceded === 0 ? (
            <p className="feel-metric feel-metric--muted">They've not scored first on us yet.</p>
          ) : (
            <p className="feel-metric">
              The North Bank goes{' '}
              {h.reaction.mood ? <b className={`mood--${h.reaction.mood}`}>{MOOD_WORD[h.reaction.mood]}</b> : 'quiet'} —
              we've recovered to a draw-or-better{' '}
              {h.reaction.metric.caveat === 'raw' ? (
                <>{h.reaction.metric.fraction} <span className="feel-n">(too early to call a trend)</span></>
              ) : (
                <>{h.reaction.metric.value}% <span className="feel-n">({h.reaction.conceded} games)</span></>
              )}
              .
            </p>
          )}
          <span className="feel-foot">Measured at the next mood reading, not the exact minute.</span>
        </div>
      </div>
    </>
  );
};

// ── The post-FT bridge: a closed thread links to its lore entry ──────────────
export const InTheBookCard = ({
  entry,
  onOpenLore,
}: {
  entry: LoreEntry;
  onOpenLore: (matchId: string) => void;
}) => (
  <button className="vault-hook in-the-book" onClick={() => onOpenLore(entry.matchId)}>
    <div className="vh-body">
      <div className="vh-label">This match is in the book</div>
      <div className="vh-text">
        Arsenal {entry.result.score.home}–{entry.result.score.away} {entry.opponent} — relive the night and the
        North Bank's read in the Vault.
      </div>
    </div>
    <span className="vh-arrow">→</span>
  </button>
);
